import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Box, Typography, Alert } from '@mui/joy';

import DeviceUuidForm from '../components/DeviceRegister/DeviceUuidForm';
import { SimpleHeaderLayout } from '../components/layout/SimpleHeaderLayout';
import { DeviceService } from '../services/DeviceService';
import { ValidationException } from '../services/Exceptions';

export default function RegisterDevice() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Register device with device UUID using DeviceService
  const registerDevice = async (deviceUuid: string) => {
    setError(false);
    setErrorText(null);
    setLoading(true);

    try {
      const response = await DeviceService.registerDevice(deviceUuid);
      if (response.success && response.data) {
        // Success - show success message and navigate back to devices page
        toast.success(t('register_device.registration_success'));
        navigate('/devices');
      }
    } catch (err: any) {
      setError(true);
      if (err instanceof ValidationException) {
        setErrorText(err.message);
      } else {
        setErrorText(err.message || 'An error occurred while registering the device');
      }
    }
    setLoading(false);
  };

  // If id param is present, trigger registration on mount
  useEffect(() => {
    if (id) {
      registerDevice(id);
    }
  }, [id, t, navigate]);

  if (!id) {
    return (
      <DeviceUuidForm
        loading={loading}
        onSubmit={(deviceUuid) => {
          navigate(`/register-device/${deviceUuid}`);
        }}
      />
    );
  }

  const cancelButton = (
    <Typography onClick={() => navigate('/devices')} sx={{ cursor: 'pointer', alignSelf: 'center', margin: '1rem' }}>
      {t('edit_profile.cancel')}
    </Typography>
  );

  return (
    <SimpleHeaderLayout controlComponent={cancelButton}>
      <Box className='flex flex-col sm:flex-row min-h-[90vh]'>
        <Box className='flex items-center justify-end sm:w-1/2 p-4'>
          <Box className='max-w-[28rem]'>
            {error ? (
              <>
                <Typography level='h1' sx={{ marginBottom: '1rem', fontSize: '2.25rem', fontWeight: '600' }}>
                  {t('register_device.error_title')}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: 'danger.100',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    color: 'danger.700',
                    fontWeight: '500'
                  }}
                >
                  {errorText}
                </Box>
                <Typography sx={{ fontWeight: '500', fontSize: '1.125rem' }}>
                  {t('register_device.instructions.contact')}
                  <a href='mailto:support@sifly.ai' style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    support@sifly.ai
                  </a>{' '}
                  {t('register_device.instructions.for_additional_help')}
                </Typography>
              </>
            ) : (
              <>
                <Typography level='h1' sx={{ marginBottom: '1rem', fontSize: '2.25rem', fontWeight: '600' }}>
                  {t('register_device.title')}
                </Typography>

                <Typography
                  sx={{ marginBottom: '0.5rem', fontWeight: '600', fontSize: '1.25rem', color: 'neutral.600' }}
                >
                  {t('register_device.instructions.title')}
                </Typography>

                <Box sx={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <Typography
                    sx={{
                      backgroundColor: 'neutral.100',
                      color: 'neutral.700',
                      height: '32px',
                      width: '32px',
                      marginRight: '0.5rem',
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    1
                  </Typography>
                  <Typography sx={{ marginBottom: '0.5rem', fontWeight: '500', fontSize: '1.125rem', width: '100%' }}>
                    {t('register_device.instructions.place_device_outside')}
                    <span style={{ textDecoration: 'underline' }}>
                      {t('register_device.instructions.strong_cellular_connectivity')}
                    </span>
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <Typography
                    sx={{
                      backgroundColor: 'neutral.100',
                      color: 'neutral.700',
                      height: '32px',
                      width: '32px',
                      marginRight: '0.5rem',
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    2
                  </Typography>
                  <Typography sx={{ marginBottom: '0.5rem', fontWeight: '500', fontSize: '1.125rem', width: '100%' }}>
                    {t('register_device.instructions.ensure_power')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', marginBottom: '0.5rem' }}>
                  <Typography
                    sx={{
                      backgroundColor: 'neutral.100',
                      color: 'neutral.700',
                      height: '32px',
                      width: '32px',
                      marginRight: '0.5rem',
                      borderRadius: '10px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    3
                  </Typography>
                  <Typography sx={{ marginBottom: '0.5rem', fontWeight: '500', fontSize: '1.125rem', width: '100%' }}>
                    {t('register_device.instructions.turn_on_device')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </SimpleHeaderLayout>
  );
}
